// components/Modal.tsx
import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** Largura máxima do conteúdo (px) */
  maxWidth?: number;
  /** Overlay transparente (true) ou escurecido (false) */
  overlayTransparent?: boolean;
  /** Padding interno à esquerda do conteúdo (px) – substitui margins inline */
  leftPadding?: number;
  /** Padding interno à direita do conteúdo (px) */
  rightPadding?: number;
  /** Padding superior/inferior do conteúdo (px) */
  verticalPadding?: number;
};

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  maxWidth = 1280,
  overlayTransparent = true,
  leftPadding = 20,
  rightPadding = 20,
  verticalPadding = 20,
}) => {
  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleKey]);

  if (!open) return null;

  return createPortal(
    <>
      <style>{`
        /* ===== Overlay ===== */
        [data-gm-modal="1"].gm-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          ${
            overlayTransparent
              ? "background: transparent;"
              : "background: rgba(0,0,0,0.5);"
          }
          ${
            overlayTransparent
              ? "backdrop-filter: none;"
              : "backdrop-filter: blur(2px);"
          }
          isolation: isolate;
        }

        /* ===== Caixa do Modal ===== */
        [data-gm-modal="1"] .gm-modal-box {
          position: relative;
          width: 100%;
          max-width: ${maxWidth}px;
          max-height: 90vh;
          background: #fff;
          border-radius: 0;
          box-shadow: 0 10px 40px rgba(0,0,0,0.4);
          margin: 0 !important;      /* evita empurrões externos */
          padding: 0;
          display: flex;
          flex-direction: column;
          left: auto !important;
          right: auto !important;
          transform: none !important;
        }

        /* ===== Cabeçalho ===== */
        [data-gm-modal="1"] .gm-modal-header {
          position: sticky;
          top: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 20px;
          border-bottom: 1px solid #ddd;
          background: #f9f9f9;
          font-weight: 600;
          z-index: 1;
        }

        /* ===== Conteúdo =====
           - padding controla o "respiro" em vez de margin-left nos filhos.
        */
        [data-gm-modal="1"] .gm-modal-content {
          flex: 1;
          background: #fff;
          text-align: left;
          padding: var(--gm-v-pad, 20px) var(--gm-r-pad, 20px) var(--gm-v-pad, 20px) var(--gm-l-pad, 100px);
          overflow: auto;
        }

        /* ===== Neutraliza margins inline (ex.: margin-left: 310px) ===== */
        [data-gm-modal="1"] .gm-modal-content [style*="margin-left"] {
          margin-left: var(--gm-l-pad, 100px) !important;
        }
        [data-gm-modal="1"] .gm-modal-content [style*="margin-right"] {
          margin-right: var(--gm-r-pad, 20px) !important;
        }
        /* Mantém um topo/calpado razoável em wrappers que insistem em margin-top inline */
        [data-gm-modal="1"] .gm-modal-content [style*="margin-top"] {
          margin-top: 12px !important;
        }

        /* ===== Botão de fechar ===== */
        [data-gm-modal="1"] .gm-modal-close {
          border: none;
          background: transparent;
          font-size: 22px;
          cursor: pointer;
          line-height: 1;
        }
      `}</style>

      <div
        className="gm-modal-overlay"
        data-gm-modal="1"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={title || "Modal"}
      >
        <div
          className="gm-modal-box"
          onClick={(e) => e.stopPropagation()}
          style={
            {
              // CSS Vars para controlar paddings e normalizar margins inline
              ["--gm-l-pad" as any]: `${leftPadding}px`,
              ["--gm-r-pad" as any]: `${rightPadding}px`,
              ["--gm-v-pad" as any]: `${verticalPadding}px`,
            } as React.CSSProperties
          }
        >
          <div className="gm-modal-header">
            <h2 style={{ margin: 0, fontSize: 18, color: "#124280" }}>
              {title}
            </h2>
            <button
              onClick={onClose}
              aria-label="Fechar"
              title="Fechar"
              className="gm-modal-close"
            >
              ×
            </button>
          </div>

          <div className="gm-modal-content">{children}</div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default Modal;
